import { useState, useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Upload, FileText, X, Loader2, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  itemId: string;
  projectId: string;
}

type Phase = 'idle' | 'processing';

export default function UploadQuoteDialog({ open, onOpenChange, itemId, projectId }: Props) {
  const queryClient = useQueryClient();

  const [file, setFile] = useState<File | null>(null);
  const [dragActive, setDragActive] = useState(false);
  const [phase, setPhase] = useState<Phase>('idle');
  const [error, setError] = useState<string | null>(null);

  const reset = useCallback(() => {
    setFile(null);
    setPhase('idle');
    setError(null);
  }, []);

  const handleDrag = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') setDragActive(true);
    else if (e.type === 'dragleave') setDragActive(false);
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    if (e.dataTransfer.files?.[0]) setFile(e.dataTransfer.files[0]);
  }, []);

  const handleSubmit = async () => {
    if (!file) return;
    setError(null);
    setPhase('processing');

    try {
      // Use /parse-and-create with itemId so parsing + insertion happen in one
      // transaction. This preserves charge formulas with `charges`/`composite`
      // bases (the synthetic `import-N` IDs are remapped to real UUIDs in the
      // same transaction). The two-step parse/save flow loses those formulas.
      const formData = new FormData();
      formData.append('file', file);
      formData.append('itemId', itemId);
      formData.append('reviewStatus', 'reviewed');

      const res = await fetch('/api/quote-import/parse-and-create', {
        method: 'POST',
        body: formData,
      });

      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.error || 'Failed to upload quote');
      }

      const created = (await res.json()) as { quoteId: string; itemId: string };

      queryClient.invalidateQueries({ queryKey: ['item', itemId] });
      queryClient.invalidateQueries({ queryKey: ['project', projectId] });
      queryClient.invalidateQueries({ queryKey: ['vendors'] });
      queryClient.invalidateQueries({ queryKey: ['project-vendors', projectId] });

      onOpenChange(false);
      reset();

      toast.success('Quote uploaded', {
        action: {
          label: 'Edit',
          onClick: () =>
            window.location.assign(
              `/projects/${projectId}/items/${itemId}/quote-editor?quoteId=${created.quoteId}`,
            ),
        },
      });
    } catch (err) {
      setPhase('idle');
      setError(err instanceof Error ? err.message : 'Failed to upload quote');
    }
  };

  const busy = phase !== 'idle';

  return (
    <Dialog
      open={open}
      onOpenChange={(o) => {
        if (!o) reset();
        onOpenChange(o);
      }}
    >
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>
            {phase === 'processing' ? 'Analyzing...' : 'Upload Quote'}
          </DialogTitle>
        </DialogHeader>

        {busy ? (
          <div className="flex flex-col items-center justify-center py-12">
            <Loader2 className="mb-4 h-8 w-8 animate-spin text-primary" />
            <p className="text-sm text-muted-foreground">Analyzing vendor quote...</p>
            <p className="mt-1 text-xs text-muted-foreground">This may take a few seconds</p>
          </div>
        ) : (
          <div className="space-y-4">
            <div
              onDragEnter={handleDrag}
              onDragLeave={handleDrag}
              onDragOver={handleDrag}
              onDrop={handleDrop}
              className={`flex flex-col items-center justify-center rounded-lg border-2 border-dashed p-8 transition-colors ${
                dragActive
                  ? 'border-primary bg-primary/5'
                  : file
                    ? 'border-primary/50 bg-primary/5'
                    : 'border-border'
              }`}
            >
              {file ? (
                <div className="flex items-center gap-2">
                  <FileText className="h-5 w-5 text-primary" />
                  <span className="text-sm font-medium">{file.name}</span>
                  <button onClick={() => setFile(null)} className="ml-2 text-muted-foreground hover:text-foreground">
                    <X className="h-4 w-4" />
                  </button>
                </div>
              ) : (
                <>
                  <Upload className="mb-2 h-8 w-8 text-muted-foreground" />
                  <p className="text-sm text-muted-foreground">
                    Drag & drop a PDF or image, or{' '}
                    <label className="cursor-pointer text-primary underline">
                      browse
                      <input
                        type="file"
                        accept=".pdf,image/jpeg,image/png"
                        className="hidden"
                        onChange={(e) => e.target.files?.[0] && setFile(e.target.files[0])}
                      />
                    </label>
                  </p>
                  <p className="mt-1 text-xs text-muted-foreground">PDF, JPEG, or PNG</p>
                </>
              )}
            </div>

            {error && (
              <div className="flex items-start gap-2 rounded-md bg-destructive/10 p-3">
                <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-destructive" />
                <p className="text-sm text-destructive">{error}</p>
              </div>
            )}

            <Button onClick={handleSubmit} disabled={!file} className="w-full">
              <Upload className="mr-1.5 h-4 w-4" />
              Upload
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
